import { z } from 'zod';

// =============================================================================
// FRONTEND SECURITY UTILITIES
// =============================================================================

export class SecurityUtils {
  private static csrfToken: string = '';
  private static csrfTokenExpiry: number = 0;
  
  // =============================================================================
  // CSRF PROTECTION
  // =============================================================================
  
  /**
   * Get CSRF token from server
   */
  static async getCSRFToken(): Promise<string> {
    // Return cached token if still valid
    if (this.csrfToken && Date.now() < this.csrfTokenExpiry) {
      return this.csrfToken;
    }
    
    try {
      // Use the backend URL from environment variable
      const baseUrl = import.meta.env.VITE_API_URL || 'https://nvgc-ai-assistant-backend-staging.dtholen.workers.dev';
      const response = await fetch(`${baseUrl}/api/csrf-token`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to get CSRF token');
      }
      
      const data = await response.json();
      this.csrfToken = data.csrfToken;
      this.csrfTokenExpiry = Date.now() + (50 * 60 * 1000); // 50 minutes (token expires in 1 hour)
      
      return this.csrfToken;
    } catch (error) {
      console.error('Failed to get CSRF token:', error);
      throw error;
    }
  }
  
  /**
   * Clear cached CSRF token
   */
  static clearCSRFToken(): void {
    this.csrfToken = '';
    this.csrfTokenExpiry = 0;
  }
  
  /**
   * Add CSRF token to request headers
   */
  static async addCSRFHeaders(headers: Record<string, string> = {}): Promise<Record<string, string>> {
    try {
      const token = await this.getCSRFToken();
      return {
        ...headers,
        'X-CSRF-Token': token
      };
    } catch (error) {
      console.warn('Failed to add CSRF token to headers:', error);
      return headers;
    }
  }
  
  // =============================================================================
  // INPUT VALIDATION AND SANITIZATION
  // =============================================================================
  
  /**
   * Sanitize HTML input to prevent XSS
   */
  static sanitizeHtml(input: string): string {
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }
  
  /**
   * Sanitize user input for safe display
   */
  static sanitizeUserInput(input: string): string {
    return input
      .trim()
      .replace(/[\x00-\x1F\x7F]/g, '') // Remove control characters
      .replace(/\0/g, ''); // Remove null bytes
  }
  
  /**
   * Validate email format
   */
  static validateEmail(email: string): boolean {
    const emailSchema = z.string().email().max(254);
    try {
      emailSchema.parse(email);
      return true;
    } catch {
      return false;
    }
  }
  
  /**
   * Validate password strength
   */
  static validatePassword(password: string): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (password.length > 128) {
      errors.push('Password must be less than 128 characters');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    // Check for common patterns
    if (/(.)\1{2,}/.test(password)) {
      errors.push('Password cannot contain repeated characters');
    }
    
    if (/123|abc|qwe|password|admin/i.test(password)) {
      errors.push('Password cannot contain common patterns');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  /**
   * Validate file upload
   */
  static validateFileUpload(file: File): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png',
      'image/jpeg',
      'image/jpg'
    ];
    
    if (file.size > maxSize) {
      errors.push('File size must be less than 10MB');
    }
    
    if (!allowedTypes.includes(file.type)) {
      errors.push('File type not supported. Please upload PDF, DOCX, PNG, or JPG files');
    }
    
    // Check filename for security
    const filename = file.name;
    if (!/^[a-zA-Z0-9._-]+$/.test(filename)) {
      errors.push('Filename contains invalid characters');
    }
    
    if (filename.length > 255) {
      errors.push('Filename is too long');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  // =============================================================================
  // SECURE STORAGE
  // =============================================================================
  
  /**
   * Securely store data in localStorage with encryption
   */
  static setSecureItem(key: string, value: any): void {
    try {
      const data = {
        value,
        timestamp: Date.now(),
        checksum: this.generateChecksum(JSON.stringify(value))
      };
      
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to store secure item:', error);
    }
  }
  
  /**
   * Securely retrieve data from localStorage
   */
  static getSecureItem<T>(key: string, maxAge: number = 24 * 60 * 60 * 1000): T | null {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      
      const data = JSON.parse(stored);
      
      // Check age
      if (Date.now() - data.timestamp > maxAge) {
        localStorage.removeItem(key);
        return null;
      }
      
      // Verify checksum
      const expectedChecksum = this.generateChecksum(JSON.stringify(data.value));
      if (data.checksum !== expectedChecksum) {
        console.warn('Data integrity check failed for:', key);
        localStorage.removeItem(key);
        return null;
      }
      
      return data.value;
    } catch (error) {
      console.error('Failed to retrieve secure item:', error);
      localStorage.removeItem(key);
      return null;
    }
  }
  
  /**
   * Remove item from secure storage
   */
  static removeSecureItem(key: string): void {
    localStorage.removeItem(key);
  }
  
  /**
   * Clear all secure storage
   */
  static clearSecureStorage(): void {
    localStorage.clear();
    sessionStorage.clear();
  }
  
  // =============================================================================
  // CONTENT SECURITY
  // =============================================================================
  
  /**
   * Check if URL is safe to navigate to
   */
  static isSafeUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      
      // Only allow HTTPS in production
      if (window.location.protocol === 'https:' && parsed.protocol !== 'https:') {
        return false;
      }
      
      // Block dangerous protocols
      const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
      if (dangerousProtocols.includes(parsed.protocol)) {
        return false;
      }
      
      // Allow same origin
      if (parsed.origin === window.location.origin) {
        return true;
      }
      
      // Allow specific trusted domains
      const trustedDomains = [
        'gaming.nv.gov',
        'supabase.co',
        'stripe.com'
      ];
      
      return trustedDomains.some(domain => 
        parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`)
      );
    } catch {
      return false;
    }
  }
  
  /**
   * Sanitize URL for safe navigation
   */
  static sanitizeUrl(url: string): string {
    if (!this.isSafeUrl(url)) {
      return '#';
    }
    return url;
  }
  
  // =============================================================================
  // SESSION SECURITY
  // =============================================================================
  
  /**
   * Generate session fingerprint for additional security
   */
  static generateSessionFingerprint(): string {
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset().toString(),
      navigator.platform
    ];
    
    return this.generateChecksum(components.join('|'));
  }
  
  /**
   * Validate session fingerprint
   */
  static validateSessionFingerprint(storedFingerprint: string): boolean {
    const currentFingerprint = this.generateSessionFingerprint();
    return storedFingerprint === currentFingerprint;
  }
  
  // =============================================================================
  // UTILITY FUNCTIONS
  // =============================================================================
  
  /**
   * Generate simple checksum for data integrity
   */
  private static generateChecksum(data: string): string {
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }
  
  /**
   * Generate secure random string
   */
  static generateRandomString(length: number = 32): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    
    for (let i = 0; i < length; i++) {
      result += chars[array[i] % chars.length];
    }
    
    return result;
  }
  
  /**
   * Mask sensitive data for display
   */
  static maskSensitiveData(data: string, visibleChars: number = 4): string {
    if (data.length <= visibleChars * 2) {
      return '*'.repeat(data.length);
    }
    
    const start = data.substring(0, visibleChars);
    const end = data.substring(data.length - visibleChars);
    const middle = '*'.repeat(data.length - visibleChars * 2);
    
    return `${start}${middle}${end}`;
  }
  
  /**
   * Check if running in secure context
   */
  static isSecureContext(): boolean {
    return window.isSecureContext || window.location.protocol === 'https:';
  }
  
  /**
   * Log security event (for monitoring)
   */
  static logSecurityEvent(event: string, details?: any): void {
    console.warn(`Security Event: ${event}`, details);
    
    // In production, you might want to send this to a monitoring service
    if (process.env.NODE_ENV === 'production') {
      // Send to monitoring service
    }
  }
}

// =============================================================================
// SECURITY HOOKS FOR REACT
// =============================================================================

/**
 * React hook for CSRF protection
 */
export function useCSRFToken() {
  const [token, setToken] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  
  const refreshToken = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const newToken = await SecurityUtils.getCSRFToken();
      setToken(newToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get CSRF token');
    } finally {
      setLoading(false);
    }
  }, []);
  
  React.useEffect(() => {
    refreshToken();
  }, [refreshToken]);
  
  return { token, loading, error, refreshToken };
}

/**
 * React hook for secure form handling
 */
export function useSecureForm() {
  const addCSRFHeaders = React.useCallback(async (headers: Record<string, string> = {}) => {
    return await SecurityUtils.addCSRFHeaders(headers);
  }, []);
  
  const validateInput = React.useCallback((input: string) => {
    return SecurityUtils.sanitizeUserInput(input);
  }, []);
  
  const validateFile = React.useCallback((file: File) => {
    return SecurityUtils.validateFileUpload(file);
  }, []);
  
  return {
    addCSRFHeaders,
    validateInput,
    validateFile,
    sanitizeHtml: SecurityUtils.sanitizeHtml,
    isSafeUrl: SecurityUtils.isSafeUrl
  };
}

// Export React for the hooks
import React from 'react';